/**
 * Karte IM 3D-RAUM (Pro-Editor-Karte, Jan 09./10.10.2026).
 *
 * Benedikts Studio-Karten liegen als HTML-Schicht ÜBER dem WebGL-Raum
 * (RendererWebGL.updateMap): nur eine Drehachse, und sie decken jede Text-,
 * Bild- und 3D-Ebene zu, egal wo die in der Zeitleiste steht. Jan wollte die
 * Karte „echt im 3D-Raum“. Deshalb:
 *
 *   MapLibre zeichnet versteckt (außerhalb des Bildes) in sein eigenes Canvas;
 *   dieses Canvas IST die Textur einer Fläche in der Szene (wie Infografik als
 *   3D-Fläche). Lage, alle drei Drehachsen, Größe, Deckkraft und Tiefe
 *   übernimmt der gewöhnliche Ebenen-Ablauf.
 *
 * GLATT (Jans Fund 10.10.2026 „so richtig smooth ist das nicht“):
 *   - keine Kopie mehr pro Bild (vorher Canvas → 2D-Canvas → Textur, auf
 *     Retina bis 3200×1800 Pixel): Nadeln + Namen zeichnet MapLibre selbst als
 *     Kartenebenen, die Quellzeile ist eine eigene kleine Fläche;
 *   - Pixel-Budget statt fester Dichte (KARTE_MAX_PIXEL);
 *   - die Karte zeichnet im SELBEN Bild wie der 3D-Raum (redraw nach jumpTo)
 *     statt ein Bild hinterher in ihrem eigenen Takt.
 *
 * Die Kartengröße in CSS-Pixeln folgt der Größe der Fläche auf dem Bildschirm,
 * damit Schrift und Straßen der Karte so groß wirken wie auf einer normalen
 * Webkarte (sonst wären sie auf einer großen Fläche winzig).
 */
import * as THREE from "three";
import type { MapContent, MapMarker } from "@scrolly/post-types";
import { kartenBild, routenLinie } from "./MapInterpolation";

/** Seitenmaß der Fläche in Szenen-Einheiten (wie Bild/Infografik). */
export const KARTE_STANDARD_GROESSE = { width: 4096, height: 4096 }; // 1:1 wie neue Karten

/** Größter Kartenausschnitt in CSS-Pixeln — mehr lädt nur Kacheln ohne Gewinn. */
const MAX_CSS_PX = 1600;

/** Pixel-Budget des Kartenbildes (Breite × Höhe × Dichte²) — jedes Bild geht zur Grafikkarte. */
export const KARTE_MAX_PIXEL = 2_000_000;

/** Weltall-Motive des SDK (CubemapLayer-Presets). */
export const WELTALL_MOTIVE = ["space", "stars", "milkyway", "milkyway-subtle", "milkyway-bright", "milkyway-colored"] as const;

/** Bilddichte: höchstens 2, höchstens Gerät, und im Pixel-Budget. */
export function kartenDichte(cssW: number, cssH: number, geraet: number): number {
    const budget = Math.sqrt(KARTE_MAX_PIXEL / Math.max(1, cssW * cssH));
    return Math.max(0.5, Math.min(2, geraet || 1, budget));
}

/** Weltall-Einstellung → SDK-Option (true = dunkler Sternenhimmel „space“). */
export function weltallOption(weltall: unknown): false | { preset: string } {
    if (!weltall) return false;
    const motiv = typeof weltall === "string" && (WELTALL_MOTIVE as readonly string[]).includes(weltall) ? weltall : "space";
    return { preset: motiv };
}

export function istKarteImRaum(content: unknown): boolean {
    return !!(content as { imRaum?: boolean } | null)?.imRaum;
}

type Karte = any; // maptilersdk.Map — das SDK wird erst bei Bedarf geladen

export class KarteImRaum {
    readonly mesh: THREE.Mesh;
    private readonly container: HTMLDivElement;
    private textur: THREE.CanvasTexture;
    /** Quellzeile als eigene kleine Fläche unten rechts (folgt der Kartenfläche). */
    private quelle: THREE.Mesh | null = null;
    private dichte = 1;
    private karte: Karte | null = null;
    private stil = "";
    private kamera = "";
    private nadeln: MapMarker[] = [];
    private cssW = 0;
    private cssH = 0;
    private weg = false;
    /** Zuletzt an die Karte gegebene Sprache/Gelände — nur Änderungen nachreichen. */
    private angewendet: { sprache?: string; gelaende: boolean; ueberhoehung?: number } = { gelaende: false };
    /** Globus/Weltall/Leuchtrand, mit denen die Karte angelegt wurde (Änderung → neu anlegen). */
    private globusStand = "";
    /** Zuletzt gezeichnete Route (Schlüssel) und ihre Punkte. */
    private routeSchluessel = "";
    private routePunkte: Array<[number, number]> = [];
    private letzteZeit: { from: number; to: number; timer: number; ort?: number } | null = null;

    constructor(
        layerId: string,
        private content: MapContent & { width?: number; height?: number },
        private readonly onBild: () => void,
    ) {
        this.container = document.createElement("div");
        // Im Dokument (MapLibre braucht echte Maße), aber außerhalb des Bildes.
        this.container.style.cssText =
            "position:fixed;left:-20000px;top:0;pointer-events:none;visibility:hidden;";
        this.container.setAttribute("aria-hidden", "true");
        this.container.dataset.karteImRaum = layerId;
        document.body.appendChild(this.container);

        // Platzhalter bis die Karte steht; danach ist ihr Canvas die Quelle
        const leer = document.createElement("canvas");
        leer.width = 2;
        leer.height = 2;
        this.textur = neueTextur(leer);

        const material = new THREE.MeshBasicMaterial({
            map: this.textur,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide,
            name: String(layerId),
            opacity: 0, // pro Bild im Ebenen-Ablauf gesetzt
        });
        const { width, height } = this.flaeche();
        const geometry = new THREE.PlaneGeometry(1, 1);
        geometry.scale(width, height, 1);
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.userData.pxW = width;
        this.mesh.userData.pxH = height;
    }

    private flaeche() {
        return {
            width: this.content.width || KARTE_STANDARD_GROESSE.width,
            height: this.content.height || KARTE_STANDARD_GROESSE.height,
        };
    }

    /** Lädt das SDK und legt die (versteckte) Karte an. */
    async starte(unitsToPx: number): Promise<void> {
        const sdk = await import("@maptiler/sdk");
        if (this.weg) return;
        sdk.config.apiKey =
            (this.content as { apiKey?: string }).apiKey ||
            (window as unknown as { __MAPTILER_API_KEY__?: string }).__MAPTILER_API_KEY__ ||
            "";
        this.setzeGroesse(unitsToPx);
        const erster = this.content.waypoints[0];
        this.stil = this.content.style;
        const karte = new sdk.Map({
            container: this.container,
            style: kartenStil(sdk, this.content.style),
            center: [erster?.lng ?? 0, erster?.lat ?? 0],
            zoom: erster?.zoom ?? 2,
            bearing: erster?.bearing ?? 0,
            pitch: erster?.pitch ?? 0,
            interactive: false,
            attributionControl: false as unknown as object,
            navigationControl: false,
            geolocateControl: false,
            // Kacheln (Jans Fund „laden sehr schlecht“): höchstens doppelte
            // Pixeldichte, kein Einblenden, mehr Kacheln im Speicher.
            pixelRatio: this.dichte,
            fadeDuration: 0,
            maxTileCacheSize: 400,
            // Neigung je Ort bis fast waagerecht (MapLibre-Vorgabe: 60°)
            maxPitch: 85,
            language: kartenSprache(sdk, this.content.sprache),
            // 3D-Gelände (Berge/Täler), Überhöhung per Regler
            terrain: !!this.content.gelaende,
            terrainExaggeration: this.content.ueberhoehung ?? 1.5,
            // Globus (Jan 10.10.2026): weit draußen Kugel, beim Hineinzoomen flach.
            // Weltall-Bild und Leuchtrand nur auf Wunsch; ohne Weltall bleibt
            // es um die Kugel durchsichtig (der 3D-Raum scheint durch).
            projection: this.content.globus ? "globe" : "mercator",
            space: this.content.globus ? weltallOption(this.content.weltall) : false,
            halo: !!(this.content.globus && this.content.leuchtrand),
            // Das Canvas ist die Textur: Inhalt muss nach dem Zeichnen stehen bleiben.
            canvasContextAttributes: { preserveDrawingBuffer: true, antialias: true },
        } as any);
        this.globusStand = globusSchluessel(this.content);
        // Nach jedem (neuen) Stil: Gelände wieder an, Route wieder einhängen —
        // ein Stilwechsel wirft beides weg.
        karte.on("style.load", () => {
            if (this.content.gelaende) {
                try { karte.enableTerrain(this.content.ueberhoehung ?? 1.5); } catch { /* egal */ }
            }
            this.routeEinrichten();
            this.nadelnEinrichten();
        });
        // Gelände sofort in voller Höhe — das SDK lässt es sonst eine Sekunde
        // aus der Fläche „wachsen“ (bei einer Scroll-Fahrt nur ein Zucken).
        try { karte.setTerrainAnimationDuration(1); } catch { /* ältere SDKs */ }
        karte.on("render", () => this.zeichne());
        // neue Canvas-Größe → Textur in der Grafikkarte neu anlegen
        karte.on("resize", () => this.texturNeu());
        this.karte = karte;
        this.texturNeu();
        this.angewendet = {
            sprache: this.content.sprache,
            gelaende: !!this.content.gelaende,
            ueberhoehung: this.content.ueberhoehung,
        };
    }

    /** Neue Inhalte aus dem Editor (Stil, Größe der Fläche, Orte). */
    aktualisiere(content: MapContent, unitsToPx: number): void {
        if (content === this.content) return;
        this.content = content;
        const { width, height } = this.flaeche();
        if (this.mesh.userData.pxW !== width || this.mesh.userData.pxH !== height) {
            this.mesh.geometry.dispose();
            const g = new THREE.PlaneGeometry(1, 1);
            g.scale(width, height, 1);
            this.mesh.geometry = g;
            this.mesh.userData.pxW = width;
            this.mesh.userData.pxH = height;
            this.setzeGroesse(unitsToPx);
        }
        const alt = this.angewendet;
        if (this.karte && (content.style !== this.stil || content.sprache !== alt.sprache
            || !!content.gelaende !== alt.gelaende || content.ueberhoehung !== alt.ueberhoehung)) {
            const stilNeu = content.style !== this.stil;
            this.stil = content.style;
            import("@maptiler/sdk").then((sdk) => {
                const karte = this.karte;
                if (!karte) return;
                try {
                    // Gelände + Route stellt danach der style.load-Hörer wieder her
                    if (stilNeu) karte.setStyle(kartenStil(sdk, content.style));
                    if (content.sprache !== alt.sprache) karte.setLanguage(kartenSprache(sdk, content.sprache));
                    if (stilNeu) {
                        // Gelände kommt mit style.load (oben)
                    } else if (!!content.gelaende !== alt.gelaende) {
                        if (content.gelaende) karte.enableTerrain(content.ueberhoehung ?? 1.5);
                        else karte.disableTerrain();
                    } else if (content.gelaende && content.ueberhoehung !== alt.ueberhoehung) {
                        karte.setTerrainExaggeration(content.ueberhoehung ?? 1.5, false);
                    }
                } catch { /* Karte noch nicht bereit */ }
            });
        }
        this.angewendet = { sprache: content.sprache, gelaende: !!content.gelaende, ueberhoehung: content.ueberhoehung };
        this.kamera = ""; // Orte evtl. geändert → nächstes Bild neu stellen
        // Globus/Weltall/Leuchtrand gibt es nur beim Anlegen → Karte neu anlegen
        if (this.karte && globusSchluessel(content) !== this.globusStand) {
            this.neuAnlegen(unitsToPx);
            return;
        }
        // Routen-Aussehen sofort übernehmen, Punkte beim nächsten Bild
        this.routeSchluessel = "";
        this.routeEinrichten();
        if (this.letzteZeit) {
            const z = this.letzteZeit;
            this.stelleKamera(z.from, z.to, z.timer, z.ort);
        }
    }

    private neuAnlegen(unitsToPx: number): void {
        try { this.karte?.remove(); } catch { /* egal */ }
        this.karte = null;
        this.kamera = "";
        this.routeSchluessel = "";
        this.cssW = 0;
        this.cssH = 0;
        void this.starte(unitsToPx);
    }

    /**
     * Route als eigene Linien-Ebene IN der Karte (zeichnet MapLibre — liegt so
     * auch auf Globus, Gelände und bei Neigung richtig). Unter den Beschriftungen,
     * damit Ortsnamen lesbar bleiben.
     */
    private routeEinrichten(): void {
        const karte = this.karte;
        if (!karte) return;
        const route = this.content.route;
        try {
            // NICHT isStyleLoaded(): das wartet auch auf Kacheln und ist bei
            // style.load noch falsch — addSource geht trotzdem, sonst fängt catch
            if (!route?.an) {
                if (karte.getLayer(ROUTE_EBENE)) karte.removeLayer(ROUTE_EBENE);
                if (karte.getSource(ROUTE_QUELLE)) karte.removeSource(ROUTE_QUELLE);
                return;
            }
            const daten = routeGeoJson(this.routePunkte);
            const quelle = karte.getSource(ROUTE_QUELLE);
            if (quelle) quelle.setData(daten);
            else karte.addSource(ROUTE_QUELLE, { type: "geojson", data: daten });
            const farbe = route.farbe || "#F02873";
            const breite = route.breite ?? 4;
            if (!karte.getLayer(ROUTE_EBENE)) {
                const ersteSchrift = (karte.getStyle()?.layers ?? []).find((l: { type: string }) => l.type === "symbol")?.id;
                karte.addLayer({
                    id: ROUTE_EBENE,
                    type: "line",
                    source: ROUTE_QUELLE,
                    layout: { "line-cap": "round", "line-join": "round" },
                    paint: { "line-color": farbe, "line-width": breite, "line-opacity": 0.95 },
                }, ersteSchrift);
            } else {
                karte.setPaintProperty(ROUTE_EBENE, "line-color", farbe);
                karte.setPaintProperty(ROUTE_EBENE, "line-width", breite);
            }
        } catch { /* Stil noch nicht bereit — style.load holt es nach */ }
    }

    /**
     * Kartengröße an die Fläche auf dem Bildschirm anpassen (gerundet, damit
     * nicht jede Kleinigkeit ein neues Kartenbild auslöst).
     */
    setzeGroesse(unitsToPx: number): void {
        const { width, height } = this.flaeche();
        const masz = Math.max(0.05, unitsToPx || 1);
        let w = width * masz;
        let h = height * masz;
        const zuGross = Math.max(w, h) / MAX_CSS_PX;
        if (zuGross > 1) {
            w /= zuGross;
            h /= zuGross;
        }
        w = Math.max(64, Math.round(w / 16) * 16);
        h = Math.max(64, Math.round(h / 16) * 16);
        if (w === this.cssW && h === this.cssH) return;
        this.cssW = w;
        this.cssH = h;
        this.container.style.width = `${w}px`;
        this.container.style.height = `${h}px`;
        this.dichte = kartenDichte(w, h, window.devicePixelRatio || 1);
        try {
            this.karte?.setPixelRatio?.(this.dichte);
            this.karte?.resize();
        } catch { /* noch nicht bereit */ }
        this.quelleSetzen();
    }

    /** Deckkraft der Fläche (pro Bild aus dem Ebenen-Ablauf) — auch für die Quellzeile. */
    setzeDeckkraft(o: number): void {
        (this.mesh.material as THREE.MeshBasicMaterial).opacity = o;
        if (this.quelle) (this.quelle.material as THREE.MeshBasicMaterial).opacity = o;
    }

    /** Textur auf das (neue) Karten-Canvas zeigen lassen. */
    private texturNeu(): void {
        const karte = this.karte;
        if (!karte) return;
        const canvas = karte.getCanvas() as HTMLCanvasElement;
        const mat = this.mesh.material as THREE.MeshBasicMaterial;
        const alt = this.textur;
        this.textur = neueTextur(canvas);
        mat.map = this.textur;
        mat.needsUpdate = true;
        alt.dispose();
    }

    /**
     * Quellzeile „Karte: © MapTiler © OpenStreetMap-Mitwirkende“ (Pflicht) als
     * eigene kleine Fläche unten rechts auf der Karte — so muss das Kartenbild
     * nicht mehr pro Bild umkopiert werden.
     */
    private quelleSetzen(): void {
        if (!this.content.quellzeile || !this.cssW) {
            if (this.quelle) {
                this.mesh.remove(this.quelle);
                (this.quelle.material as THREE.MeshBasicMaterial).map?.dispose();
                (this.quelle.material as THREE.Material).dispose();
                this.quelle.geometry.dispose();
                this.quelle = null;
            }
            return;
        }
        const { width, height } = this.flaeche();
        const einheitJePx = width / this.cssW; // Szenen-Einheiten je CSS-Pixel der Karte
        if (!this.quelle) {
            const bild = quellzeileBild();
            const tex = neueTextur(bild.canvas);
            const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide, opacity: 0 });
            this.quelle = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
            this.quelle.userData.cssW = bild.cssW;
            this.quelle.userData.cssH = bild.cssH;
            this.quelle.renderOrder = 1;
            this.mesh.add(this.quelle);
        }
        const qw = this.quelle.userData.cssW * einheitJePx;
        const qh = this.quelle.userData.cssH * einheitJePx;
        this.quelle.scale.set(qw, qh, 1);
        this.quelle.position.set(width / 2 - qw / 2 - 6 * einheitJePx, -height / 2 + qh / 2 + 4 * einheitJePx, 0.5);
    }

    /**
     * Nadeln + Namen als Kartenebenen (zeichnet MapLibre ins selbe Bild; liegen
     * so auch auf Globus/Gelände richtig): Hof, Punkt mit weißem Rand, Name auf
     * weißem Schild — wie Benedikts Beat „Ort“.
     */
    private nadelnEinrichten(): void {
        const karte = this.karte;
        if (!karte) return;
        try {
            const daten = nadelGeoJson(this.nadeln);
            const q = karte.getSource(NADEL_QUELLE);
            if (q) q.setData(daten);
            else karte.addSource(NADEL_QUELLE, { type: "geojson", data: daten });
            if (!karte.hasImage?.(SCHILD_BILD)) {
                const s = schildBild();
                karte.addImage(SCHILD_BILD, s.bild, s.optionen);
            }
            const schrift = (karte.getStyle()?.layers ?? [])
                .find((l: any) => l.type === "symbol" && Array.isArray(l.layout?.["text-font"]))?.layout?.["text-font"];
            if (!karte.getLayer(NADEL_HOF)) {
                karte.addLayer({
                    id: NADEL_HOF, type: "circle", source: NADEL_QUELLE,
                    paint: { "circle-radius": 13, "circle-color": ["get", "farbe"], "circle-opacity": 0.18 },
                });
                karte.addLayer({
                    id: NADEL_PUNKT, type: "circle", source: NADEL_QUELLE,
                    paint: {
                        "circle-radius": 5, "circle-color": ["get", "farbe"],
                        "circle-stroke-color": "#ffffff", "circle-stroke-width": 3,
                    },
                });
                karte.addLayer({
                    id: NADEL_NAME, type: "symbol", source: NADEL_QUELLE,
                    layout: {
                        "text-field": ["get", "name"],
                        ...(schrift ? { "text-font": schrift } : {}),
                        "text-size": 17,
                        "text-anchor": "top",
                        "text-offset": [0, 1.6],
                        "text-allow-overlap": true,
                        "icon-allow-overlap": true,
                        "icon-image": SCHILD_BILD,
                        "icon-text-fit": "both",
                        "icon-text-fit-padding": [5, 11, 5, 11],
                        "icon-anchor": "top",
                    },
                    paint: { "text-color": "#1A1A18" },
                });
            }
        } catch { /* Stil noch nicht bereit — style.load holt es nach */ }
    }

    /**
     * Pro Bild: Kamera aus der Zeit. Nur bei echter Änderung bewegen (Jans
     * Kachel-Fund: vorher bekam die Karte jedes Bild einen neuen Auftrag).
     */
    stelleKamera(from: number, to: number, timer: number, ort?: number): void {
        const karte = this.karte;
        if (!karte) return;
        this.letzteZeit = { from, to, timer, ort };
        const { kamera: k, nadeln } = kartenBild(this.content.waypoints, from, to, timer, ort);
        // Route: wächst mit der Fahrt (Ort als Kommazahl) oder steht ganz
        const route = this.content.route;
        if (route?.an) {
            const punkte = routenLinie(this.content.waypoints, route.mitwachsen === false ? undefined : ort, !!route.bogen);
            const rs = punkte.length + ":" + (punkte[punkte.length - 1] ?? []).map((v) => v.toFixed(5)).join(",");
            if (rs !== this.routeSchluessel) {
                this.routeSchluessel = rs;
                this.routePunkte = punkte;
                try { karte.getSource(ROUTE_QUELLE)?.setData(routeGeoJson(punkte)); } catch { /* noch nicht bereit */ }
            }
        }
        const schluessel = [k.lng, k.lat, k.zoom, k.bearing, k.pitch].map((v) => v.toFixed(5)).join(",");
        const nadelSchluessel = JSON.stringify(nadeln);
        const nadelnNeu = nadelSchluessel !== JSON.stringify(this.nadeln);
        this.nadeln = nadeln;
        if (nadelnNeu) {
            try { karte.getSource(NADEL_QUELLE)?.setData(nadelGeoJson(nadeln)); } catch { /* noch nicht bereit */ }
        }
        if (schluessel === this.kamera && !nadelnNeu) return;
        this.kamera = schluessel;
        try {
            karte.jumpTo({ center: [k.lng, k.lat], zoom: k.zoom, bearing: k.bearing, pitch: k.pitch });
            // JETZT zeichnen (nicht im eigenen Takt ein Bild später) — so passt
            // das Kartenbild genau zu diesem Bild des 3D-Raums.
            karte.redraw();
        } catch { /* Karte noch nicht bereit */ }
    }

    /** Neues Kartenbild → Textur beim nächsten 3D-Bild hochladen (ohne Kopie). */
    private zeichne(): void {
        if (!this.karte || this.weg) return;
        this.textur.needsUpdate = true;
        this.onBild();
    }

    entferne(): void {
        this.weg = true;
        try { this.karte?.remove(); } catch { /* egal */ }
        this.container.remove();
        this.textur.dispose();
        if (this.quelle) {
            (this.quelle.material as THREE.MeshBasicMaterial).map?.dispose();
            (this.quelle.material as THREE.Material).dispose();
            this.quelle.geometry.dispose();
        }
        this.mesh.geometry.dispose();
        (this.mesh.material as THREE.Material).dispose();
    }
}

const ROUTE_QUELLE = "scrolly-route";
const ROUTE_EBENE = "scrolly-route-linie";

function routeGeoJson(punkte: Array<[number, number]>): any {
    return {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: punkte.length >= 2 ? punkte : [] },
    };
}

function globusSchluessel(c: MapContent): string {
    return [!!c.globus, c.globus ? String(c.weltall ?? false) : "", !!(c.globus && c.leuchtrand)].join(",");
}

/**
 * Stil-ID → MapTiler-Stil. „dataviz“ = Grundstil, „dataviz.dark“ = Variante
 * (Jan 10.10.2026: mehr Stile + Varianten dunkel/hell/pastell/vintage …).
 * Unbekanntes geht als Stil-ID/URL durch, leer → Straßenkarte.
 */
export function kartenStilAufloesen(sdk: any, stil: string | undefined): any {
    if (!stil) return sdk.MapStyle.STREETS;
    const [basis, variante] = stil.split(".");
    const ref = sdk.MapStyle[basis.toUpperCase()];
    if (!ref) return stil;
    return (variante && ref[variante.toUpperCase()]) || ref;
}

/** Sprache der Beschriftung: 'de' | 'en' | 'local' (Landessprache), sonst wie der Browser. */
export function kartenSprache(sdk: any, sprache: string | undefined): any {
    switch (sprache) {
        case "de": return sdk.Language.GERMAN;
        case "en": return sdk.Language.ENGLISH;
        case "local": return sdk.Language.LOCAL;
        default: return sdk.Language.AUTO;
    }
}

const kartenStil = kartenStilAufloesen;

const NADEL_QUELLE = "scrolly-nadeln";
const NADEL_HOF = "scrolly-nadel-hof";
const NADEL_PUNKT = "scrolly-nadel-punkt";
const NADEL_NAME = "scrolly-nadel-name";
const SCHILD_BILD = "scrolly-schild";

function neueTextur(quelle: HTMLCanvasElement): THREE.CanvasTexture {
    const t = new THREE.CanvasTexture(quelle);
    t.colorSpace = THREE.SRGBColorSpace;
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    return t;
}

function nadelGeoJson(nadeln: MapMarker[]): any {
    return {
        type: "FeatureCollection",
        features: nadeln
            .filter((n) => n.label)
            .map((n) => ({
                type: "Feature",
                properties: { name: n.label, farbe: n.color ? `#${n.color.replace("#", "")}` : "#F02873" },
                geometry: { type: "Point", coordinates: [n.lng, n.lat] },
            })),
    };
}

/** Weißes Schild mit runden Ecken, dehnbar (icon-text-fit). */
function schildBild(): { bild: ImageData; optionen: any } {
    const d = 2;
    const g = 32 * d;
    const c = document.createElement("canvas");
    c.width = g;
    c.height = g;
    const ctx = c.getContext("2d")!;
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    rundesRechteck(ctx, 0, 0, g, g, 7 * d);
    ctx.fill();
    return {
        bild: ctx.getImageData(0, 0, g, g),
        optionen: {
            pixelRatio: d,
            stretchX: [[8 * d, 24 * d]],
            stretchY: [[8 * d, 24 * d]],
            content: [8 * d, 8 * d, 24 * d, 24 * d],
        },
    };
}

/** Quellzeile als kleines Bild (doppelte Dichte für Schärfe). */
function quellzeileBild(): { canvas: HTMLCanvasElement; cssW: number; cssH: number } {
    const text = "Karte: © MapTiler © OpenStreetMap-Mitwirkende";
    const d = 3;
    const c = document.createElement("canvas");
    const ctx0 = c.getContext("2d")!;
    ctx0.font = `500 ${9 * d}px "DM Sans", system-ui, sans-serif`;
    const tw = ctx0.measureText(text).width;
    const pad = 5 * d;
    c.width = Math.ceil(tw + pad * 2);
    c.height = 14 * d;
    const ctx = c.getContext("2d")!;
    ctx.fillStyle = "rgba(255,255,255,0.72)";
    rundesRechteck(ctx, 0, 0, c.width, c.height, 3 * d);
    ctx.fill();
    ctx.font = `500 ${9 * d}px "DM Sans", system-ui, sans-serif`;
    ctx.fillStyle = "#3c3c3c";
    ctx.textBaseline = "middle";
    ctx.fillText(text, pad, c.height / 2);
    return { canvas: c, cssW: c.width / d, cssH: c.height / d };
}

function rundesRechteck(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}
